'use client';

/**
 * Tier Simulator Hook
 *
 * Mirrors the masquerade pattern but for license tiers.
 * Lets admins preview the UI as if they had a different Monaiq license tier
 * (Essentials, Professional, Enterprise).
 *
 * State stored in HTTP-only cookie (like masquerade) so the licensing
 * proxy route can read it and override the Monaiq response.
 */

import { useState, useEffect, useCallback } from 'react';
import {
  useOptionalAdminStatus,
  useOptionalAppShell,
} from '@/components/shell/AppShellContext';
import type { LicenseTier } from '@/lib/licensing/types';
import { TIER_DISPLAY } from '@/lib/licensing/types';
import logger from '@/lib/logger';
import { dedupedFetchJson, invalidateDedupedFetch } from '@/lib/utils/dedupedFetch';

const SIMULATOR_URL = '/api/licensing/simulator';

export interface UseTierSimulatorResult {
  /** Whether tier simulation is currently active */
  isSimulating: boolean;
  /** The simulated tier (null when not simulating) */
  simulatedTier: LicenseTier | null;
  /** Whether the user has permission to simulate tiers */
  canSimulate: boolean;
  /** Available tiers to simulate */
  availableTiers: Array<{ value: LicenseTier; label: string; description: string }>;
  /** Activate tier simulation */
  activateSimulation: (tier: LicenseTier) => Promise<void>;
  /** End tier simulation */
  deactivateSimulation: () => Promise<void>;
  /** Loading state */
  loading: boolean;
}

const AVAILABLE_TIERS: Array<{ value: LicenseTier; label: string; description: string }> = [
  { value: 'essentials', ...TIER_DISPLAY.essentials },
  { value: 'professional', ...TIER_DISPLAY.professional },
  { value: 'enterprise', ...TIER_DISPLAY.enterprise },
];

export function useTierSimulator(): UseTierSimulatorResult {
  const adminStatus = useOptionalAdminStatus();
  const appShell = useOptionalAppShell();

  const [isSimulating, setIsSimulating] = useState(false);
  const [simulatedTier, setSimulatedTier] = useState<LicenseTier | null>(null);
  const [loading, setLoading] = useState(false);

  // Only platform admins and users with @neutralaiz.com emails can simulate
  const canSimulate = adminStatus?.isPlatformAdmin ?? false;
  const authVersion = appShell?.authVersion ?? 0;

  const loadSimulationState = useCallback(async () => {
    if (!canSimulate) {
      setIsSimulating(false);
      setSimulatedTier(null);
      return;
    }

    try {
      const data = await dedupedFetchJson<{ simulating?: boolean; tier?: LicenseTier }>(
        SIMULATOR_URL,
        { throwOnError: false },
      );
      if (data?.simulating && data.tier) {
        setIsSimulating(true);
        setSimulatedTier(data.tier);
      } else {
        setIsSimulating(false);
        setSimulatedTier(null);
      }
    } catch {
      setIsSimulating(false);
      setSimulatedTier(null);
    }
  }, [canSimulate]);

  useEffect(() => {
    void loadSimulationState();
  }, [authVersion, loadSimulationState]);

  // Sync state across instances when simulation changes (mirrors masquerade pattern)
  useEffect(() => {
    const handler = () => {
      void loadSimulationState();
    };
    window.addEventListener('tier-simulator-changed', handler);
    return () => window.removeEventListener('tier-simulator-changed', handler);
  }, [loadSimulationState]);

  const activateSimulation = useCallback(async (tier: LicenseTier) => {
    setLoading(true);
    try {
      const res = await fetch('/api/licensing/simulator', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tier }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || `Failed to activate simulation: ${res.status}`);
      }

      setIsSimulating(true);
      setSimulatedTier(tier);

      // Invalidate BEFORE dispatching so the handler's loadSimulationState
      // refetch hits the network, not the stale cache.
      invalidateDedupedFetch(SIMULATOR_URL);
      window.dispatchEvent(new CustomEvent('tier-simulator-changed'));

      logger.info('TierSimulator', `Activated tier simulation: ${tier}`);
    } catch (err) {
      logger.error('TierSimulator', 'Failed to activate simulation', {
        error: err instanceof Error ? err.message : String(err),
      });
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const deactivateSimulation = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/licensing/simulator', { method: 'DELETE' });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || `Failed to deactivate simulation: ${res.status}`);
      }

      setIsSimulating(false);
      setSimulatedTier(null);

      invalidateDedupedFetch(SIMULATOR_URL);
      window.dispatchEvent(new CustomEvent('tier-simulator-changed'));

      logger.info('TierSimulator', 'Deactivated tier simulation');
    } catch (err) {
      logger.error('TierSimulator', 'Failed to deactivate simulation', {
        error: err instanceof Error ? err.message : String(err),
      });
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    isSimulating,
    simulatedTier,
    canSimulate,
    availableTiers: AVAILABLE_TIERS,
    activateSimulation,
    deactivateSimulation,
    loading,
  };
}
